import { useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { HANDLE_WIDTH } from './layoutConstants';

interface ResizeHandleProps {
  /** Called with the incremental horizontal delta (px) as the pointer moves. */
  onResize: (deltaPx: number) => void;
  /** Drag lifecycle — lets the parent suppress its column transition mid-drag. */
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
  ariaLabel: string;
}

/**
 * A thin vertical drag handle that sits in its own grid column between a sidebar
 * and the map. Uses native Pointer Events with pointer capture so the drag keeps
 * tracking even when the cursor crosses the MapLibre canvas / attribution iframe.
 * Clamping is the parent's responsibility — this just forwards raw deltas.
 */
export function ResizeHandle({ onResize, onResizeStart, onResizeEnd, ariaLabel }: ResizeHandleProps) {
  const [hover, setHover] = useState(false);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);

  const begin = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    startX.current = e.clientX;
    setDragging(true);
    onResizeStart?.();
    document.body.style.userSelect = 'none';
  };

  const move = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const delta = e.clientX - startX.current;
    if (delta === 0) return;
    startX.current = e.clientX;
    onResize(delta);
  };

  const end = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setDragging(false);
    onResizeEnd?.();
    document.body.style.userSelect = '';
  };

  const active = hover || dragging;

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      onPointerDown={begin}
      onPointerMove={move}
      onPointerUp={end}
      onLostPointerCapture={end}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: HANDLE_WIDTH,
        height: '100%',
        cursor: 'col-resize',
        touchAction: 'none',
        display: 'flex',
        justifyContent: 'center',
        background: 'transparent',
      }}
    >
      <div
        style={{
          width: active ? 2 : 1,
          height: '100%',
          background: dragging
            ? 'var(--accent)'
            : active
              ? 'var(--border-hover)'
              : 'var(--border)',
          transition: 'background 120ms ease, width 120ms ease',
        }}
      />
    </div>
  );
}
